import React, { useState, useRef, useCallback, useEffect } from "react";
import { FaHeadset, FaShieldAlt, FaPlay, FaPause, FaCheck, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { cmsApi } from "../../services/cms";

type OverviewImage = {
  src: string;
  alt: string;
};

type OverviewSectionProps = {
  name: string;
  title: string;
  paragraphs: string[];
  keyHighlights?: string[];
  images: OverviewImage[];
  videos?: string[];
};

const OverviewSection: React.FC<OverviewSectionProps> = ({
  name,
  // title,
  paragraphs,
  keyHighlights: keyHighlightsProp = [],
  images,
  videos = [],
}) => {
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [allPaused, setAllPaused] = useState(false);
  const [apiHighlights, setApiHighlights] = useState<string[]>([]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    if (!name) return;
    const slug = name.toLowerCase().replace(/\s+/g, '-');
    cmsApi.getDestinationHighlights(slug)
      .then((res: any) => {
        // Handle multiple possible response shapes
        const raw = res?.data ?? res;
        if (Array.isArray(raw) && raw.length > 0) {
          // Shape: [{ highlight: "..." }] or ["..."]
          const mapped = raw.map((h: any) =>
            typeof h === 'string' ? h : (h.highlight ?? h.text ?? h.title ?? JSON.stringify(h))
          );
          setApiHighlights(mapped);
        } else if (Array.isArray(res?.keyHighlights)) {
          // Shape: { keyHighlights: ["..."] }
          setApiHighlights(res.keyHighlights);
        }
      })
      .catch(() => {});
  }, [name]);

  const keyHighlights = apiHighlights.length > 0 ? apiHighlights : keyHighlightsProp;

  const pauseAllInline = useCallback(() => {
    videoRefs.current.forEach((v) => v?.pause());
  }, []);

  const playAllInline = useCallback(() => {
    videoRefs.current.forEach((v) => v?.play());
  }, []);

  const handleCenterButton = () => {
    if (allPaused) {
      playAllInline();
      setAllPaused(false);
    } else {
      pauseAllInline();
      setAllPaused(true);
    }
  };

  const openModal = (index: number) => {
    pauseAllInline();
    setActiveVideoIndex(index);
    setVideoOpen(true);
  };

  const closeModal = () => {
    setVideoOpen(false);
    if (!allPaused) playAllInline();
  };
  const primaryImage =
    images[0] ??
    ({
      src: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80",
      alt: `${name} view 1`,
    } satisfies OverviewImage);

  const resolvedImages =
    images.length >= 4
      ? images
      : Array.from({ length: 4 }, (_, index) => images[index] ?? {
          src: primaryImage.src,
          alt: `${name} view ${index + 1}`,
        });

  const resolvedParagraphs =
    paragraphs.length >= 2
      ? paragraphs
      : [
          paragraphs[0] || `Discover ${name} with curated journeys.`,
          "Our luxury itineraries are crafted to deliver authentic, premium experiences.",
        ];

  return (
    <section id="overview" className="py-16 md:py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* LEFT CONTENT */}
          <div className="lg:col-span-5">

            <span className="text-brand-pink font-semibold font-heading tracking-wider uppercase text-2xl mb-4 block">
              Destination Overview
            </span>

            {/* {title && (
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-brand-dark mb-6 leading-tight">
                {title}
              </h2>
            )} */}

             <div className="mb-8 space-y-5">
              {resolvedParagraphs.map((paragraph, index) => (
                <p
                  key={`${name}-overview-${index}`}
                  className="text-gray-600 text-base leading-relaxed font-light"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* KEY HIGHLIGHTS */}
            {keyHighlights.length > 0 && (
              <div className="mb-8">
                <h3 className="font-heading text-lg font-bold text-brand-dark mb-4">Key Highlights</h3>
                <ul className="space-y-3">
                  {keyHighlights.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-brand-pink/10 flex items-center justify-center shrink-0 mt-0.5">
                        <FaCheck className="text-brand-pink text-[10px]" />
                      </div>
                      <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* TRUST */}
            <div className="flex flex-wrap gap-6 pt-6 border-t border-gray-100">

              {/* Item 1 */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-brand-gray flex items-center justify-center text-brand-pink">
                  <FaHeadset />
                </div>
                <div>
                  <p className="text-sm font-semibold text-brand-dark">
                    24/7 Concierge
                  </p>
                  <p className="text-xs text-gray-500">
                    Dedicated local support
                  </p>
                </div>
              </div>

              {/* Item 2 */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-brand-gray flex items-center justify-center text-brand-pink">
                  <FaShieldAlt />
                </div>
                <div>
                  <p className="text-sm font-semibold text-brand-dark">
                    Visa Assistance
                  </p>
                  <p className="text-xs text-gray-500">
                    Hassle-free processing
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* RIGHT IMAGES / VIDEOS */}
          <div className="lg:col-span-7 relative">

            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-4 md:space-y-6 mt-8 md:mt-12">
                <img className="w-full h-48 sm:h-64 md:h-80 object-cover rounded-2xl shadow-lg" src={resolvedImages[0].src} alt={resolvedImages[0].alt} />
                {videos[2] ? (
                  <div
                    className="relative w-full h-32 sm:h-48 md:h-64 rounded-2xl shadow-lg overflow-hidden cursor-pointer group"
                    onClick={() => openModal(2)}
                  >
                    <video ref={(el) => { videoRefs.current[2] = el; }} src={videos[2]} className="w-full h-full object-cover" muted loop playsInline autoPlay />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <FaPlay className="text-white text-2xl" />
                    </div>
                  </div>
                ) : (
                  <img className="w-full h-32 sm:h-48 md:h-64 object-cover rounded-2xl shadow-lg" src={resolvedImages[1].src} alt={resolvedImages[1].alt} />
                )}
              </div>
              <div className="space-y-4 md:space-y-6">
                {/* bottom-right slot: video[0] if available, else image[2] */}
                {videos[0] ? (
                  <div
                    className="relative w-full h-32 sm:h-48 md:h-64 rounded-2xl shadow-lg overflow-hidden cursor-pointer group"
                    onClick={() => openModal(0)}
                  >
                    <video ref={(el) => { videoRefs.current[0] = el; }} src={videos[0]} className="w-full h-full object-cover" muted loop playsInline autoPlay />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <FaPlay className="text-white text-2xl" />
                    </div>
                  </div>
                ) : (
                  <img className="w-full h-32 sm:h-48 md:h-64 object-cover rounded-2xl shadow-lg" src={resolvedImages[2].src} alt={resolvedImages[2].alt} />
                )}
                {/* bottom-right slot 2: video[1] if available, else image[3] */}
                {videos[1] ? (
                  <div
                    className="relative w-full h-48 sm:h-64 md:h-80 rounded-2xl shadow-lg overflow-hidden cursor-pointer group"
                    onClick={() => openModal(1)}
                  >
                    <video ref={(el) => { videoRefs.current[1] = el; }} src={videos[1]} className="w-full h-full object-cover" muted loop playsInline autoPlay />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <FaPlay className="text-white text-2xl" />
                    </div>
                  </div>
                ) : (
                  <img className="w-full h-48 sm:h-64 md:h-80 object-cover rounded-2xl shadow-lg" src={resolvedImages[3].src} alt={resolvedImages[3].alt} />
                )}
              </div>
            </div>

            {/* CENTER BUTTON — pause/play all inline videos */}
            {videos.length > 0 && (
              <button
                onClick={handleCenterButton}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-brand-pink shadow-2xl hover:scale-110 transition group z-10"
              >
                {allPaused
                  ? <FaPlay className="text-2xl ml-1 group-hover:text-brand-dark transition" />
                  : <FaPause className="text-2xl group-hover:text-brand-dark transition" />
                }
              </button>
            )}

          </div>
        </div>
      </div>

      {/* VIDEO MODAL */}
      {videoOpen && videos.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center px-4" onClick={closeModal}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>

            {/* Close */}
            <button onClick={closeModal} className="absolute -top-10 right-0 text-white hover:text-brand-pink transition">
              <FaTimes className="text-2xl" />
            </button>

            {/* Video */}
            <video
              key={videos[activeVideoIndex]}
              src={videos[activeVideoIndex]}
              controls
              autoPlay
              className="w-full rounded-2xl shadow-2xl"
            />

            {/* Prev / Next */}
            {videos.length > 1 && (
              <>
                <button
                  onClick={() => setActiveVideoIndex((activeVideoIndex - 1 + videos.length) % videos.length)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 w-10 h-10 bg-white/20 hover:bg-brand-pink rounded-full flex items-center justify-center text-white transition"
                >
                  <FaChevronLeft />
                </button>
                <button
                  onClick={() => setActiveVideoIndex((activeVideoIndex + 1) % videos.length)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 w-10 h-10 bg-white/20 hover:bg-brand-pink rounded-full flex items-center justify-center text-white transition"
                >
                  <FaChevronRight />
                </button>
              </>
            )}

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {videos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveVideoIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition ${
                    i === activeVideoIndex ? "bg-brand-pink" : "bg-white/40 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default OverviewSection;
